'use client'

import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { useNavigationContext } from '@/contexts/NavigationContext'
import { INTENT_CARDS, MacroIntent } from '@/types/navigation.types'

export function IntentRouterModal() {
  const router = useRouter()
  const {
    intentMenuVisible,
    closeIntentMenu,
    activateIntent,
    currentIntent,
    completeOnboarding,
  } = useNavigationContext()

  if (!intentMenuVisible) return null

  const handleSelectIntent = (intentId: MacroIntent) => {
    const route = activateIntent(intentId)
    completeOnboarding()
    if (route) {
      router.push(route)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={closeIntentMenu}
      />

      {/* Modal */}
      <div className="relative bg-dark-secondary border border-dark-border rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
            <i className="fa-solid fa-compass text-3xl text-primary-blue"></i>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">What would you like to do?</h2>
          <p className="text-dark-text-secondary">
            Choose your focus and we'll guide you through the best workflow
          </p>
        </div>

        {/* Intent Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {INTENT_CARDS.map(intent => (
            <button
              key={intent.id}
              onClick={() => handleSelectIntent(intent.id)}
              className={`group p-4 rounded-xl border transition-all text-left ${
                currentIntent === intent.id
                  ? 'bg-primary-blue/20 border-primary-blue/50'
                  : 'bg-dark-tertiary/50 border-dark-border hover:border-primary-blue/30 hover:bg-dark-tertiary'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  currentIntent === intent.id
                    ? 'bg-primary-blue/30'
                    : 'bg-dark-tertiary group-hover:bg-primary-blue/20'
                }`}>
                  <i className={`${intent.icon} ${
                    currentIntent === intent.id ? 'text-primary-blue' : 'text-dark-text-secondary group-hover:text-primary-blue'
                  }`}></i>
                </div>
                <div>
                  <h3 className={`font-medium ${
                    currentIntent === intent.id ? 'text-white' : 'text-white group-hover:text-white'
                  }`}>
                    {intent.title}
                  </h3>
                  <p className="text-sm text-dark-text-secondary mt-1">
                    {intent.description}
                  </p>
                </div>
              </div>
              {currentIntent === intent.id && (
                <div className="mt-3 flex items-center text-primary-blue text-sm">
                  <i className="fa-solid fa-check-circle mr-1"></i>
                  Currently selected
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-dark-border">
          <Button
            variant="ghost"
            className="text-dark-text-secondary"
            onClick={closeIntentMenu}
          >
            <i className="fa-solid fa-times mr-2"></i>
            Cancel
          </Button>
          <p className="text-xs text-dark-text-secondary">
            You can change this anytime from the navigation
          </p>
        </div>
      </div>
    </div>
  )
}
